<template>
  <div class="content content-overview">
    <div class="container-">
      <breadcrumbs :breadcrumbs="breadcrumbs" />

      <template v-if="!categoryBody">
        <div class="category-header container">
          <h1>{{ categoryLocaleData.name }}{{ $t("category_view_suffix") }}</h1>
        </div>
        <div class="categories-overview container">
          <div v-for="group in subcategoriesGroups">
            <nuxt-link
              v-for="subcategory in group"
              :to="localePath(getCategoryUrl(subcategory))"
              :key="`subcategory-${subcategory.id}`"
            >
              {{ getCategoryLocaleData(subcategory).name }}
            </nuxt-link>
          </div>
        </div>
      </template>
      <template v-else>
        <Content
          v-if="categoryBody && categoryBody.length"
          :page="{ content: categoryBody }"
          :category="category"
        />
      </template>
      
    </div>
  </div>
</template>

<script>
import { rememberExpiresMixin, headCatalogOverviewMixin } from "@/mixins";
import { DbConfig } from "@/models/db-config";
import { Category } from "@/models/category";

import Breadcrumbs from "@/components/breadcrumbs";
import Content from "@/components/content";
import { Seo } from "@/models/seo";

const _subcategoriesColumns = 3;

export default {
  components: {
    Breadcrumbs,
    Content,
  },
  mixins: [rememberExpiresMixin, headCatalogOverviewMixin],
  computed: {
    metaTitleSuffix() {
      return this.$t("category_view_suffix");
    },
    breadcrumbs() {
      let breadcrumbs = [];
      let category = this.category;
      let categoryTree = [category];
      while (category.parent) {
        categoryTree.push(category.parent);
        category = category.parent;
      }
      let categoryUrl = "/";
      for (const category of categoryTree.reverse()) {
        categoryUrl += `${category.slug}/`;
        if (category.parent || categoryTree.length === 1) {
          breadcrumbs.push({
            name: this.categoryLocaleData.name,
            link: categoryUrl,
          });
        }
      }
      breadcrumbs[breadcrumbs.length - 1].name += this.$t(
        "category_view_suffix"
      );
      return breadcrumbs;
    },
    categoryLocaleData() {
      return (
        this.category.category_translations?.find(
          (x) => x.language.slug === this.$i18n.locale
        ) || {}
      );
    },
    subcategoriesGroups() {
      const groupLength = Math.ceil(
        this.category.subcategories.length / _subcategoriesColumns
      );
      let result = [];
      for (let i = 0; i < _subcategoriesColumns; i++) {
        result.push(
          this.category.subcategories.slice(
            i * groupLength,
            (i + 1) * groupLength
          )
        );
      }
      return result;
    },
    categoryBody() {
      return this.seo?.category_seo_text_obzor?.length
        ? this.seo.category_seo_text_obzor[0].body
        : null;
    },
  },
  async asyncData(context) {
    const slug = context.route.params.slug;
    try {
      const [dbConfig, category] = await Promise.all([
        DbConfig.config({ count_loading: true }).first(),
        Category.config({ count_loading: true }).find(slug),
      ]);
      const seo = await Seo.config({ count_loading: true })
        .params({ category: category.id })
        .first();
      return {
        dbConfig: dbConfig,
        category: category,
        seo: seo,
      };
    } catch (e) {
      context.error({ statusCode: e.response.status });
    }
  },
  methods: {
    getCategoryLocaleData(category) {
      return (
        category.category_translations?.find(
          (x) => x.language.slug === this.$i18n.locale
        ) || {}
      );
    },
    getCategoryUrl(category) {
      let c = this.category;
      let tree = [category, c];
      while (c.parent) {
        tree.push(c.parent);
        c = c.parent;
      }
      return (
        "/" +
        tree
          .reverse()
          .map((x) => x.slug)
          .join("/") +
        "/"
      );
    },
  },
};
</script>

<i18n lang="yaml">
ru:
  category_view_suffix: ", обзор"
en:
  category_view_suffix: ", overview"
</i18n>
